import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Upload, CheckCircle2 } from "lucide-react";
import { Reveal, MaskLine, SectionLabel } from "@/components/Reveal";
import api from "@/lib/api";

const inputCls =
  "w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/15";

const FIELDS = [
  { key: "name", label: "Full Name", required: true, span: false },
  { key: "email", label: "Email Address", required: true, type: "email", span: false },
  { key: "phone", label: "Phone Number", required: true, span: false },
  { key: "current_location", label: "Current Location", span: false },
  { key: "total_experience", label: "Total Experience", placeholder: "e.g. 5 years", span: false },
  { key: "designation", label: "Current / Most Recent Designation", span: false },
  { key: "preferred_location", label: "Preferred Location", span: false },
  { key: "skills", label: "Area of Expertise / Skills", placeholder: "e.g. React, Node.js, Finance", span: false },
];

export default function ApplyHere() {
  const [params] = useSearchParams();
  const role = params.get("role") || "";
  const empty = { name: "", email: "", phone: "", current_location: "", total_experience: "", designation: "", preferred_location: "", skills: "", message: "" };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Full name is required";
    if (!form.email.trim()) errs.email = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address";
    if (!form.phone.trim()) errs.phone = "Phone number is required";
    if (file && file.size > 5 * 1024 * 1024) errs.resume = "Resume must be under 5 MB";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (role) data.append("applied_for", role);
      if (file) data.append("resume", file);
      await api.post("/candidates", data);
      setDone(true);
      toast.success("Application submitted successfully.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="pt-[124px] pb-10 lg:pt-[150px] lg:pb-14 relative overflow-hidden" data-testid="apply-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full grid grid-cols-2 lg:grid-cols-4">
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60 hidden lg:block" />
            <div className="border-l border-r border-slate-200/60 hidden lg:block" />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskLine delay={0.05}>
            <span className="flex items-center gap-4">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-brand-red font-medium">Find Jobs · Apply Now</span>
            </span>
          </MaskLine>
          <h1 className="mt-7 font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl">
            <MaskLine delay={0.18}>Build Your Career</MaskLine>
            <MaskLine delay={0.3}>With <span className="text-brand-red">Genspire.</span></MaskLine>
          </h1>
          <MaskLine delay={0.44} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              We're always looking to connect with talented professionals. If you're exploring your next career move, share your details and resume with us. Our team will review your profile and connect with you when a suitable opportunity matches your experience and aspirations.
            </p>
          </MaskLine>
        </div>
      </section>

      <section className="pb-14 lg:pb-20 pt-4" data-testid="apply-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {role && (
            <Reveal>
              <div className="mb-8 flex items-center gap-3 border border-brand-red/30 bg-brand-light/50 px-5 py-4" data-testid="apply-role-banner">
                <span className="h-1.5 w-1.5 rotate-45 bg-brand-red shrink-0" />
                <p className="text-sm text-slate-700">Applying for: <span className="font-semibold text-slate-900">{role}</span></p>
              </div>
            </Reveal>
          )}

          {done ? (
            <Reveal>
              <div className="border border-slate-200 p-10 sm:p-14 text-center" data-testid="apply-success">
                <span className="inline-flex p-4 rounded-full bg-brand-light text-brand-red"><CheckCircle2 size={28} /></span>
                <h2 className="mt-6 font-display font-bold text-slate-900 text-2xl sm:text-3xl">Application Received.</h2>
                <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-md mx-auto">
                  Thank you for sharing your profile with Genspire. Our team will review your details and connect with you when a suitable opportunity matches your experience and aspirations.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                  <Link to="/careers/openings" data-testid="apply-success-browse"
                    className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-3.5 transition-colors duration-300">
                    Browse Current Openings
                  </Link>
                  <button onClick={() => { setDone(false); setForm(empty); setFile(null); }} data-testid="apply-success-another"
                    className="inline-flex items-center gap-2 border border-slate-300 hover:border-brand-red text-slate-800 hover:text-brand-red font-medium text-sm px-8 py-3.5 transition-colors duration-300">
                    Submit Another Application
                  </button>
                </div>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <div className="border border-slate-200 p-7 sm:p-10">
                <SectionLabel>Candidate Application</SectionLabel>
                <form onSubmit={submit} data-testid="apply-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5" noValidate>
                  {FIELDS.map((f) => (
                    <div key={f.key} className={f.span ? "sm:col-span-2" : ""}>
                      <input
                        type={f.type || "text"}
                        placeholder={f.placeholder || f.label + (f.required ? " *" : "")}
                        value={form[f.key]}
                        onChange={set(f.key)}
                        className={`${inputCls} ${errors[f.key] ? "border-brand-red ring-2 ring-brand-red/15" : ""}`}
                        data-testid={`apply-${f.key.replace(/_/g, "-")}-input`}
                      />
                      {errors[f.key] && <p className="mt-1.5 text-xs text-brand-red" data-testid={`apply-${f.key}-error`}>{errors[f.key]}</p>}
                    </div>
                  ))}

                  <div className="sm:col-span-2">
                    <textarea placeholder="Additional Message" rows={4} value={form.message} onChange={set("message")}
                      className={`${inputCls} resize-none`} data-testid="apply-message-input" />
                  </div>

                  <div className="sm:col-span-2">
                    <label data-testid="apply-resume-upload"
                      className={`flex items-center justify-center gap-3 rounded-md border-2 border-dashed px-4 py-6 text-sm cursor-pointer transition-colors duration-300 ${
                        errors.resume ? "border-brand-red text-brand-red" : "border-slate-300 text-slate-500 hover:border-brand-red hover:text-brand-red"
                      }`}>
                      <Upload size={18} />
                      {file ? file.name : "Upload your resume (PDF, DOC, DOCX — max 5 MB)"}
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                        onChange={(e) => { setFile(e.target.files[0] || null); if (errors.resume) setErrors({ ...errors, resume: null }); }} />
                    </label>
                    {errors.resume && <p className="mt-1.5 text-xs text-brand-red">{errors.resume}</p>}
                  </div>

                  <button type="submit" disabled={loading} data-testid="apply-submit-button"
                    className="sm:col-span-2 group inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                    {loading ? "Submitting…" : "Submit Application"}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
